import isDate from 'lodash/isDate';

import { Category, IPublication, IPublicationContentItem, IPublicationIssue } from '@pressreader/catalog-types';
import { parseDateString } from '@pressreader/utils';

import { IIssueDto, IPublicationDto } from './types';

function mapPublicationIssueFromDto(dto: IIssueDto): IPublicationIssue {
    return {
        key: dto.key,
        id: dto.id,
        cid: dto.cid,
        version: dto.version,
        expungeVersion: dto.expungeVersion,
        issueDate: isDate(dto.issueDate) ? dto.issueDate : parseDateString(dto.issueDate),
        firstPage: {
            width: dto.firstPage.width,
            height: dto.firstPage.height,
        },
    };
}

const typeMap: Record<string, IPublication['type']> = {
    Newspaper: 'Newspaper',
    '1': 'Newspaper',
    Magazine: 'Magazine',
    '2': 'Magazine',
    Book: 'Book',
    '3': 'Book',
    Comics: 'Comics',
    Feed: 'Feed',
    Audiobook: 'Audiobook',
    Game: 'Game',
    OnlineEdition: 'OnlineEdition',
};

const mapPublicationType = (input: string | number): IPublication['type'] => {
    const key = String(input);
    if (!(key in typeMap)) {
        throw new Error(`Unknown publication type: ${input}`);
    }
    return typeMap[key];
};

function mapPublicationFromDto(dto: IPublicationDto): IPublication {
    return {
        id: dto.id,
        cid: dto.cid,
        prn: dto.prn,
        type: mapPublicationType(dto.type),
        name: dto.name,
        displayName: dto.displayName || dto.name,
        displayNameLang: dto.displayNameLang,
        alternateNames: dto.alternateNames ?? [],
        shortDescription: dto.shortDescription,
        shortDescriptionLang: dto.shortDescriptionLang,
        fullDescription: dto.fullDescription,
        fullDescriptionLang: dto.fullDescriptionLang,
        isMagazine: dto.categories.some(cat => cat === Category.Magazines),
        isFree: !!dto.isFree,
        isSmart: !!dto.isSmart,
        isVirtual: !!dto.isVirtual,
        rank: dto.rank,
        slug: dto.slug,
        countrySlug: dto.countrySlug,
        isSupplement: dto.isSupplement,
        issn: dto.issn,
        supplements: Array.isArray(dto.supplements) ? dto.supplements.map(mapPublicationFromDto) : [],
        otherRegionalEditions: Array.isArray(dto.otherRegionalEditions) ? dto.otherRegionalEditions.map(mapPublicationFromDto) : [],
        categories: dto.categories,
        latestIssue: mapPublicationIssueFromDto(dto.latestIssue),
        publicationCountry: dto.publicationCountry,
        countries: dto.countries,
        language: dto.language,
        mastheads: dto.mastheads,
        publisher: dto.publisher ? { id: dto.publisher.id, name: dto.publisher.name, slug: dto.publisher.slug } : undefined,
        original: dto.original,
        availability: dto.availability,
        schedule: dto.schedule,
    };
}

/**
 * Wraps a publication DTO into the catalog content-item envelope consumed across the catalog/search UI
 * (`{ type, id, data }`). Matches the envelope `search-filters`' `loadPublicationsData` builds, so items
 * from this mapper and from that saga are interchangeable to their consumers.
 */
function mapPublicationDtoToContentItem(dto: IPublicationDto): IPublicationContentItem {
    return {
        type: 'Publication',
        id: dto.cid,
        data: mapPublicationFromDto(dto),
    };
}

export { mapPublicationDtoToContentItem, mapPublicationFromDto };
